import {
  Button,
  CurrencyInput,
  Field,
  FileInput,
  Modal,
  ModalProps,
  RawInput,
  Select,
  SelectSizeVariant,
  Textarea,
} from '@payaca/ambrosia';
import { FC, useMemo, type JSX } from 'react';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { useFileUploadContext } from '@/context/FileUploadContext';
import { zodResolver } from '@hookform/resolvers/zod';

import { getAcceptedFileTypes } from '@payaca/helpers/fileHelper';
import { formatUrlWithProtocol } from '@payaca/helpers/urlHelper';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import useCreateMaterial from '../../../api/mutations/materials/useCreateMaterial';
import useGetMaterialCategories from '../../../api/queries/materials/useGetMaterialCategories';
import useGetMyTaxRates from '../../../api/queries/me/useGetMyTaxRates';
import { CreateMaterialMutation } from '../../../gql/graphql';
import SupplierSelectionControl from '../supplierSelectionControl/SupplierSelectionControl';

const FORM_SCHEMA = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(500, 'Name must be 500 characters or less'),
  description: z.string().optional(),
  categoryId: z.string().optional(),
  image: z
    .object({
      file: z.any(),
      preview: z.string(),
    })
    .optional(),
  suppliedBy: z
    .array(
      z.object({
        supplierId: z.string(),
        unitPriceExcTax: z.number().int(),
        taxRateId: z.string(),
        url: z.string().optional(),
        reference: z.string().optional(),
      })
    )
    .min(1),
});

export type TFormState = z.infer<typeof FORM_SCHEMA>;

const CreateMaterialBody: FC<Omit<CreateMaterialModalProps, 'isOpen'>> = (
  props
): JSX.Element => {
  const { onClose, onPersistMaterialSuccess, material, suppliedBy } = props;
  const { data: regionPreferences } = useGetMyRegionPreferences();
  const { defaultTaxRate, taxRates: allTaxRates } = useGetMyTaxRates();
  const { materialCategories } = useGetMaterialCategories();
  const { mutateAsync: createMaterialMutation } = useCreateMaterial();
  const { enqueueUploads } = useFileUploadContext();

  const taxRates = allTaxRates?.filter((t) => !t.isReverseCharge);

  const formMethods = useForm<TFormState>({
    resolver: zodResolver(FORM_SCHEMA),
    defaultValues: {
      name: material?.name || '',
      suppliedBy: [
        {
          supplierId: suppliedBy?.supplierId
            ? `${suppliedBy.supplierId}`
            : undefined,
          unitPriceExcTax: suppliedBy?.unitPriceExcTax,
          taxRateId: suppliedBy?.taxRateId ?? defaultTaxRate?.id,
          url: suppliedBy?.url,
          reference: suppliedBy?.reference,
        },
      ],
    },
  });

  const onSubmit = async (state: TFormState) => {
    let imageUploadIntentId: string | undefined = undefined;
    if (state.image?.file) {
      const uploadIntents = await enqueueUploads([{ file: state.image.file }]);

      imageUploadIntentId = uploadIntents[0]?.id;
    }

    const body = {
      name: state.name,
      description: state.description,
      categoryId: state.categoryId,
      suppliedBy: state.suppliedBy.map((sb) => ({
        ...sb,
        url: sb.url ? formatUrlWithProtocol(sb.url) : undefined,
        reference: sb.reference || undefined,
      })),
      imageUploadIntentId,
    };

    const { createMaterial } = await createMaterialMutation(body);

    onPersistMaterialSuccess?.(createMaterial);
    onClose?.();
  };

  const materialCategoryOptions = useMemo(() => {
    return (
      materialCategories?.map((x) => {
        return {
          label: x.name,
          value: x.id,
        };
      }) || []
    );
  }, [materialCategories]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <Modal.Body className="flex flex-col gap-4">
          <Field.Legacy>
            <Field.Label>Thumbnail</Field.Label>

            <Controller
              name="image"
              render={({ field: { onChange, value } }) => {
                return (
                  <FileInput
                    label={'Upload image'}
                    url={value?.preview}
                    onDrop={(file) => {
                      onChange({
                        file,
                        preview: URL.createObjectURL(file),
                      });
                    }}
                    onDelete={() => {
                      onChange({
                        file: undefined,
                      });
                    }}
                    dropzoneOptions={{
                      accept: getAcceptedFileTypes(['image']),
                    }}
                  />
                );
              }}
            />
          </Field.Legacy>

          <Field.Legacy
            validationState={
              formMethods.formState.errors.name
                ? {
                    isValid: false,
                    validationMessages: [
                      formMethods.formState.errors.name.message ??
                        'Name is required',
                    ],
                  }
                : undefined
            }
          >
            <Field.Label>Material name</Field.Label>
            <RawInput {...formMethods.register('name')} />
          </Field.Legacy>

          <Field.Legacy>
            <Field.Label>Description</Field.Label>
            <Controller
              name="description"
              render={({ field: { onChange, value } }) => {
                return (
                  <Textarea
                    value={value}
                    onChange={onChange}
                    rows={1}
                    autoHeight
                  />
                );
              }}
            />
          </Field.Legacy>

          <Field.Legacy>
            <Field.Label>Category</Field.Label>
            <Controller
              name="categoryId"
              render={({ field: { onChange, value } }) => {
                return (
                  <Select
                    value={value}
                    onChange={onChange}
                    options={materialCategoryOptions}
                  />
                );
              }}
            />
          </Field.Legacy>

          <Field.Legacy
            validationState={
              formMethods.formState.errors.suppliedBy?.[0]?.supplierId
                ? {
                    isValid: false,
                    validationMessages: ['Supplier is required'],
                  }
                : undefined
            }
          >
            <Field.Label>Supplier</Field.Label>
            <Controller
              name="suppliedBy[0].supplierId"
              render={({ field: { onChange, value } }) => {
                return (
                  <SupplierSelectionControl
                    selectedSupplierId={value ? +value : undefined}
                    onChange={(c) => {
                      onChange(`${c}`);
                    }}
                    enableSupplierCreation
                    excludeSystemManaged
                    canChangeSupplier={!suppliedBy?.supplierId}
                  />
                );
              }}
            />
          </Field.Legacy>

          <div className="flex gap-x-4">
            <Field.Legacy
              validationState={
                formMethods.formState.errors.suppliedBy?.[0]?.unitPriceExcTax
                  ? {
                      isValid: false,
                      validationMessages: ['Price is required'],
                    }
                  : undefined
              }
            >
              <Field.Label>Price (ex tax)</Field.Label>
              <Controller
                name="suppliedBy[0].unitPriceExcTax"
                render={({ field: { onChange, value } }) => {
                  return (
                    <CurrencyInput
                      sizeVariant="sm"
                      inputClassName="text-right"
                      value={value}
                      onChange={onChange}
                      currency={regionPreferences?.currency}
                    />
                  );
                }}
              />
            </Field.Legacy>
            <Field.Legacy
              validationState={
                formMethods.formState.errors.suppliedBy?.[0]?.taxRateId
                  ? {
                      isValid: false,
                      validationMessages: ['Tax is required'],
                    }
                  : undefined
              }
            >
              <Field.Label>Tax</Field.Label>
              <Controller
                name="suppliedBy[0].taxRateId"
                defaultValue={
                  taxRates?.find((taxRate) => taxRate.isDefault)?.id
                }
                render={({ field: { onChange, onBlur, value, ref } }) => {
                  return (
                    <Select
                      sizeVariant={SelectSizeVariant.SM}
                      value={value}
                      onChange={onChange}
                      options={[
                        ...(taxRates
                          ?.filter((taxRate) => !taxRate.isReverseCharge)
                          .map((taxRate) => ({
                            label: taxRate.label,
                            value: taxRate.id,
                          })) || []),
                        ...(taxRates
                          ?.filter((taxRate) => taxRate.isReverseCharge)
                          .map((taxRate) => ({
                            label: taxRate.label,
                            value: taxRate.id,
                          })) || []),
                      ]}
                    />
                  );
                }}
              />
            </Field.Legacy>
          </div>
          <Field.Legacy>
            <Field.Label>Material URL</Field.Label>
            <RawInput {...formMethods.register('suppliedBy.0.url')} />
          </Field.Legacy>
          <Field.Legacy>
            <Field.Label>Supplier reference</Field.Label>
            <RawInput {...formMethods.register('suppliedBy.0.reference')} />
          </Field.Legacy>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions className="space-x-2">
            <Button onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </form>
    </FormProvider>
  );
};

export type CreateMaterialModalProps = {
  onPersistMaterialSuccess?: (
    material: CreateMaterialMutation['createMaterial']
  ) => void;
  material?: { name: string };
  /**
   * A prefilled supplier is locked, because the form that prefilled it has
   * already settled which supplier this material is being priced for.
   */
  suppliedBy?: {
    supplierId?: number;
    unitPriceExcTax?: number;
    taxRateId?: string;
    url?: string;
    reference?: string;
  };
} & Pick<ModalProps, 'isOpen' | 'onClose'>;

const CreateMaterialModal: FC<CreateMaterialModalProps> = (props) => {
  const { isOpen, onClose, ...rest } = props;

  return (
    <Modal
      isOpen={isOpen}
      title="Create material"
      onClose={onClose}
      size="md"
      disableBackdropClick
    >
      <CreateMaterialBody onClose={onClose} {...rest} />
    </Modal>
  );
};
export default CreateMaterialModal;
